/**
 * TIMED HOLD PROGRESSION — duration as the progression lever.
 *
 * Planks, hollow holds, side planks, dead hangs, wall sits. 165 of this
 * athlete's logged exercises are `timed`, the third-largest tracking type
 * after weight and bodyweight — and nothing in the engine had ever moved one.
 * Every adaptive path reasons about weight or reps, so a hold was prescribed
 * at whatever the program authored, forever, no matter how easy it got.
 *
 * Same shape as lib/carryProgression.ts, because it is the same problem: the
 * progression variable is neither load nor reps. Ordinary double progression
 * with DURATION standing in for reps:
 *
 *   1. Work up the authored duration range at a fixed difficulty.
 *   2. On owning the top of the range at a manageable effort, either add load
 *      (weighted planks) or advance the movement, and reset the clock.
 *   3. If effort is high or the hold broke early, HOLD — never cut.
 *
 * Why hold rather than cut: an isometric fails by postural breakdown, not by
 * gradual slowing. Someone who breaks at 35 of 45 seconds has not lost
 * capacity the way a missed squat rep signals it — their brace gave out.
 * Cutting the target there teaches the engine to chase noise, which is exactly
 * what produced the escalating phantom deload on the carry.
 *
 * Pure and I/O-free so it can be tested against real logged history.
 */

/** Parsed duration prescription, in seconds. */
export interface TimedHoldSpec {
  /** Bottom of the authored range (or the single authored value). */
  min: number;
  /** Top of the authored range. Equals `min` when a single value was authored. */
  max: number;
  /** True when the program authored an explicit range like '30-45s'. */
  isRange: boolean;
}

export interface TimedHoldHistoryPoint {
  date: Date;
  /** Seconds actually held for this set. */
  seconds: number;
  /** External load, lb. 0 for an unweighted hold. */
  weight: number;
  rpe: number | null;
  /** Whether the set was completed as prescribed. */
  wasSuccessful: boolean;
}

export type TimedProgressionAction =
  | 'extend-duration'
  | 'add-load'
  | 'hold'
  | 'insufficient-data';

export interface TimedProgressionResult {
  action: TimedProgressionAction;
  /** Duration to prescribe per set, seconds. */
  seconds: number;
  /** Load to prescribe, lb. 0 stays 0 for unweighted holds. */
  weight: number;
  /** Plain-language explanation. Never says "reps". */
  reason: string;
  confidence: 'high' | 'medium' | 'low';
}

/**
 * Ceiling for a single authored duration with no range.
 *
 * Isometric endurance has sharply diminishing returns — past roughly a
 * minute a plank trains tolerance for discomfort more than it trains the
 * brace. 2x the authored hold keeps a 45s prescription inside a 45-90s band
 * rather than marching it toward a five-minute plank, which is a different
 * (and largely pointless) exercise.
 */
const IMPLICIT_RANGE_FACTOR = 2;

/** Hard ceiling on any prescribed hold. Beyond this, add load instead. */
export const MAX_HOLD_SECONDS = 120;

/** Smallest step worth prescribing. Below this it is stopwatch noise. */
const MIN_STEP_S = 5;

const HARD_EFFORT_RPE = 9;
const EASY_EFFORT_RPE = 8;

/** Load step once duration tops out, for holds that can be loaded. */
const LOAD_STEP_LB = 5;

/**
 * Parse an authored hold duration: '45s', '30-45s', '3 × 45s', '1:00', '2 min'.
 * Returns null when the string carries no duration we can trust.
 */
export function parseHoldDuration(setsReps: string): TimedHoldSpec | null {
  if (!setsReps) return null;

  // Strip a leading "sets ×" so '3 × 45s' doesn't read 3 as the duration.
  const afterSets = setsReps.replace(/^\s*\d+\s*[x×]\s*/i, '');

  const toSeconds = (value: number, unit: string | undefined): number =>
    unit && /^m/i.test(unit) ? value * 60 : value;

  // Range: '30-45s' or '1-2 min'
  const range = afterSets.match(
    /(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)\s*(s|secs?|seconds?|m|mins?|minutes?)\b/i,
  );
  if (range) {
    const unit = range[3];
    const min = toSeconds(parseFloat(range[1]), unit);
    const max = toSeconds(parseFloat(range[2]), unit);
    if (min > 0 && max >= min) return { min, max, isRange: true };
  }

  // MM:SS
  const clock = afterSets.match(/(\d+):(\d{2})\b/);
  if (clock) {
    const v = parseInt(clock[1], 10) * 60 + parseInt(clock[2], 10);
    if (v > 0) return { min: v, max: v, isRange: false };
  }

  // Single value with a unit: '45s', '2 min'
  const single = afterSets.match(/(\d+(?:\.\d+)?)\s*(s|secs?|seconds?|m|mins?|minutes?)\b/i);
  if (single) {
    const v = toSeconds(parseFloat(single[1]), single[2]);
    if (v > 0) return { min: v, max: v, isRange: false };
  }

  // A bare number is NOT assumed to be seconds. On a timed exercise it is far
  // more likely to be a mis-authored rep count, and inventing a duration from
  // it would be worse than declining to progress.
  return null;
}

/** Effective ceiling for a spec — explicit range top, or the implicit band. */
export function holdCeiling(spec: TimedHoldSpec): number {
  const raw = spec.isRange ? spec.max : Math.round(spec.min * IMPLICIT_RANGE_FACTOR);
  return Math.min(MAX_HOLD_SECONDS, raw);
}

/**
 * Per-session summaries. A hold session is judged on the duration the athlete
 * SUSTAINED across the work, not their single best set — three sets at 45s and
 * one at 20s is not a 45-second session.
 */
function summariseSessions(history: TimedHoldHistoryPoint[]) {
  const byDate = new Map<string, TimedHoldHistoryPoint[]>();
  for (const h of history) {
    const key = h.date.toISOString().slice(0, 10);
    const bucket = byDate.get(key);
    if (bucket) bucket.push(h);
    else byDate.set(key, [h]);
  }

  return Array.from(byDate.entries())
    .map(([date, sets]) => {
      const rpes = sets.map(s => s.rpe).filter((r): r is number => r != null && r > 0);
      const completed = sets.filter(s => s.wasSuccessful);
      return {
        date,
        weight: Math.max(...sets.map(s => s.weight), 0),
        sustainedSeconds: completed.length > 0
          ? Math.min(...completed.map(s => s.seconds))
          : 0,
        avgRPE: rpes.length > 0 ? rpes.reduce((a, b) => a + b, 0) / rpes.length : null,
        allCompleted: sets.length > 0 && completed.length === sets.length,
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Decide the next hold prescription.
 *
 * @param spec       authored duration prescription for this session
 * @param baseWeight external load the engine would otherwise prescribe (0 = unweighted)
 * @param history    logged set-level history for this hold, any order
 * @param canLoad    whether this movement accepts external load (a plank can;
 *                   a dead hang generally shouldn't be progressed that way here)
 */
export function getTimedHoldProgression(
  spec: TimedHoldSpec,
  baseWeight: number,
  history: TimedHoldHistoryPoint[],
  canLoad = true,
): TimedProgressionResult {
  const ceiling = holdCeiling(spec);
  const sessions = summariseSessions(history);
  const last = sessions[sessions.length - 1];

  if (!last || last.sustainedSeconds <= 0) {
    return {
      action: 'insufficient-data',
      seconds: spec.min,
      weight: baseWeight,
      reason: `Starting at ${spec.min}s — log a session to start progressing.`,
      confidence: 'low',
    };
  }

  const effort = last.avgRPE;
  const struggled = !last.allCompleted || (effort != null && effort >= HARD_EFFORT_RPE);

  if (struggled) {
    const held = Math.max(spec.min, Math.min(last.sustainedSeconds, ceiling));
    const why = !last.allCompleted
      ? `you didn't finish every set last time`
      : `last session ran RPE ${effort!.toFixed(1)}`;
    return {
      action: 'hold',
      seconds: held,
      weight: baseWeight,
      reason: `Holding ${held}s — ${why}. Own this before it grows.`,
      confidence: 'high',
    };
  }

  const sustained = last.sustainedSeconds;

  // Topped out at a manageable effort → add load (if the movement takes it)
  // and reset the clock. An unloadable hold simply stays at the ceiling:
  // marching a plank past two minutes trains discomfort tolerance, not bracing.
  if (sustained >= ceiling) {
    if (!canLoad) {
      return {
        action: 'hold',
        seconds: ceiling,
        weight: baseWeight,
        reason: `Holding at ${ceiling}s — long enough. Progress this by making the position harder, not the clock longer.`,
        confidence: 'high',
      };
    }
    const nextWeight = baseWeight + LOAD_STEP_LB;
    return {
      action: 'add-load',
      seconds: spec.min,
      weight: nextWeight,
      reason: `You held ${sustained}s — the top of the range. Add ${LOAD_STEP_LB} lb and reset the clock to ${spec.min}s.`,
      confidence: 'high',
    };
  }

  if (effort == null || effort <= EASY_EFFORT_RPE) {
    const step = Math.max(MIN_STEP_S, Math.round((sustained * 0.15) / 5) * 5);
    const next = Math.min(ceiling, sustained + step);
    if (next <= sustained) {
      return {
        action: 'hold',
        seconds: sustained,
        weight: baseWeight,
        reason: `Holding ${sustained}s.`,
        confidence: 'medium',
      };
    }
    return {
      action: 'extend-duration',
      seconds: next,
      weight: baseWeight,
      reason: effort == null
        ? `Up to ${next}s — you completed every set last time.`
        : `Up to ${next}s — last session sat at RPE ${effort.toFixed(1)}.`,
      confidence: effort == null ? 'medium' : 'high',
    };
  }

  return {
    action: 'hold',
    seconds: Math.max(spec.min, sustained),
    weight: baseWeight,
    reason: `Repeating ${Math.max(spec.min, sustained)}s — RPE ${effort.toFixed(1)} says bank this one first.`,
    confidence: 'medium',
  };
}

/** Rewrite an authored sets_reps string with a new per-set duration. */
export function applyHoldDuration(setsReps: string, seconds: number): string {
  const setsMatch = setsReps.match(/^\s*(\d+)\s*[x×]/i);
  const sets = setsMatch ? setsMatch[1] : null;
  const label = seconds >= 60 && seconds % 60 === 0 ? `${seconds / 60} min` : `${seconds}s`;
  return sets ? `${sets} × ${label}` : label;
}
